import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButton as MuiToggleButton,
  type ToggleButtonGroupProps as MuiToggleButtonGroupProps,
} from '@mui/material';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

export type ToggleButtonOption = {
  value: string;
  label: string | React.ReactNode;
};

export type ToggleButtonGroupProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  MuiToggleButtonGroupProps,
  'value' | 'onChange'
> & {
  options: ToggleButtonOption[];
  control?: Control<TFieldValues>;
  fieldPath?: FieldPath<TFieldValues>;
  value?: string;
  onChange?: (value: string) => void;
};

const ToggleButtonGroup = <TFieldValues extends FieldValues = FieldValues>(
  props: ToggleButtonGroupProps<TFieldValues>,
) => {
  const { options, control, fieldPath, value, onChange, ...muiProps } = props;

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null,
    rhfOnChange?: (value: unknown) => void,
  ) => {
    if (newValue !== null) {
      rhfOnChange?.(newValue);
      onChange?.(newValue);
    }
  };

  if (control && fieldPath) {
    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <MuiToggleButtonGroup
            {...muiProps}
            value={field.value}
            exclusive
            onChange={(e, newValue) => handleChange(e, newValue, field.onChange)}
          >
            {options.map(option => (
              <MuiToggleButton key={option.value} value={option.value}>
                {option.label}
              </MuiToggleButton>
            ))}
          </MuiToggleButtonGroup>
        )}
      />
    );
  }

  return (
    <MuiToggleButtonGroup
      {...muiProps}
      value={value}
      exclusive
      onChange={(e, newValue) => handleChange(e, newValue, undefined)}
    >
      {options.map(option => (
        <MuiToggleButton key={option.value} value={option.value}>
          {option.label}
        </MuiToggleButton>
      ))}
    </MuiToggleButtonGroup>
  );
};

export default ToggleButtonGroup;
